import {
    getMenteeRoleService,
    updateMenteeAreaStatusService,
    updateMenteeAreaLinkStateService,
    updateMenteeElementMediatorService,
    getAreaService,
} from '../services/roleService';
import { getKnowledgeElementService } from '../services/traineeService';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { MENTOR_ID } from '../constants';

export const getMenteeRole = createAsyncThunk(
  'role/getMenteeRole',
  async ({ menteeId, roleId }, thunkAPI) => {
    try {
      const response = await getMenteeRoleService(menteeId, roleId);
      return {
        ...response.data,
        mentor: response.data.mentor || '',
      };
    } catch (error) {
      console.error('API Error:', error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateKnowledgeElement = createAsyncThunk(
    'role/updateKnowledgeElement',
    async ({ personId, elementId }, thunkAPI) => {
        try {
            const response = await getKnowledgeElementService(personId, elementId);
            return {
                elementId,
                element: response.data.knowledgeElements[elementId]
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateArea = createAsyncThunk(
    'role/updateArea',
    async ({ personId, roleId, areaId }, thunkAPI) => {
        try {
            const response = await getAreaService(personId, roleId, areaId);
            return {
                areaId: response.data.knowledgeArea.id,
                area: response.data.knowledgeArea,
                completionPercentage: response.data.completionPercentage
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateMenteeElementMediator = createAsyncThunk(
    'role/updateMenteeElementMediator',
    async ({ menteeId, roleId, areaId, elementId, newMediatorId }, thunkAPI) => {
        try {
            const response = await updateMenteeElementMediatorService(menteeId, roleId, areaId, elementId, { mediatorId: newMediatorId });
            return {
                elementId,
                areaId,
                updatedKnowledgeElement: response.data.updatedKnowledgeElement[elementId],
                updatedKnowledgeArea: response.data.updatedKnowledgeArea[areaId]
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateMenteeAreaStatus = createAsyncThunk(
    'role/updateMenteeAreaStatus',
    async ({ menteeId, roleId, areaId, status }, thunkAPI) => {
        try {
            const response = await updateMenteeAreaStatusService(menteeId, roleId, areaId, { status });
            return {
                areaId,
                completionPercentage: response.data.completionPercentage,
                updatedKnowledgeArea: response.data.updatedKnowledgeArea[areaId],
                updatedKnowledgeElements: response.data.updatedKnowledgeElements
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateMenteeAreaLinkState = createAsyncThunk(
    'role/updateMenteeAreaLinkState',
    async ({ menteeId, roleId, areaId, link }, thunkAPI) => {
        try {
            const response = await updateMenteeAreaLinkStateService(menteeId, roleId, areaId, { linkToEmployee: link });
            return {
                areaId,
                completionPercentage: response.data.completionPercentage,
                updatedKnowledgeArea: response.data.updatedKnowledgeArea[areaId]
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

const roleInitialState = {
    additionalKnowledgeAreasSortOrder: [],
    categories: {},
    completionPercentage: 0,
    description: '',
    id: null,
    isLoading: false,
    knowledgeAreas: {},
    knowledgeAreasSortOrder: [],
    knowledgeElements: {},
    menteeId: '',
    mentor: '',
    name: '',
    persons: {},
    wildcardPersons: {
        '00000000-0000-0000-0000-000000000001': {
            id: '00000000-0000-0000-0000-000000000001',
            name: 'Person themself',
            initials: 'S',
            email: '',
        },
        '00000000-0000-0000-0000-000000000002': {
            id: '00000000-0000-0000-0000-000000000002',
            name: 'Knowledge owner',
            initials: 'K',
            email: '',
        },
        '00000000-0000-0000-0000-000000000003': {
            id: '00000000-0000-0000-0000-000000000003',
            name: 'Expert',
            initials: 'E',
            email: '',
        },
        [MENTOR_ID]: {
            id: MENTOR_ID,
            name: 'Mentor',
            initials: 'M',
            email: '',
        },
        '00000000-0000-0000-0000-000000000004': {
            id: '00000000-0000-0000-0000-000000000004',
            name: 'Deleted person',
            initials: 'N/A',
            email: '',
        },
    },
};

const roleSlice = createSlice({
    name: 'role',
    initialState: roleInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getMenteeRole.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getMenteeRole.fulfilled, (state, action) => {
                return {
                    ...state,
                    ...action.payload,
                    mentor: action.payload.mentor || '',
                    isLoading: false
                };
            })
            .addCase(getMenteeRole.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateKnowledgeElement.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateKnowledgeElement.fulfilled, (state, action) => {
                const { elementId, element } = action.payload;
                state.knowledgeElements[elementId] = element;
                state.isLoading = false;
            })
            .addCase(updateKnowledgeElement.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateArea.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateArea.fulfilled, (state, action) => {
                const { areaId, area, completionPercentage } = action.payload;
                state.knowledgeAreas[areaId] = area;
                state.completionPercentage = completionPercentage;
                state.isLoading = false;
            })
            .addCase(updateArea.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateMenteeElementMediator.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateMenteeElementMediator.fulfilled, (state, action) => {
                const { elementId, areaId, updatedKnowledgeElement, updatedKnowledgeArea } = action.payload;
                state.knowledgeElements[elementId] = updatedKnowledgeElement;
                state.knowledgeAreas[areaId] = updatedKnowledgeArea;
                state.isLoading = false;
            })
            .addCase(updateMenteeElementMediator.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateMenteeAreaStatus.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateMenteeAreaStatus.fulfilled, (state, action) => {
                const { areaId, completionPercentage, updatedKnowledgeArea, updatedKnowledgeElements } = action.payload;
                state.completionPercentage = completionPercentage;
                state.knowledgeAreas[areaId] = updatedKnowledgeArea;
                state.knowledgeElements = {
                    ...state.knowledgeElements,
                    ...updatedKnowledgeElements
                };
                state.isLoading = false;
            })
            .addCase(updateMenteeAreaStatus.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateMenteeAreaLinkState.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateMenteeAreaLinkState.fulfilled, (state, action) => {
                const { areaId, completionPercentage, updatedKnowledgeArea } = action.payload;
                state.completionPercentage = completionPercentage;
                state.knowledgeAreas[areaId] = updatedKnowledgeArea;
                state.isLoading = false;
            })
            .addCase(updateMenteeAreaLinkState.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export default roleSlice.reducer;
export const roleActions = roleSlice.actions;